import { Alert, Box, Typography, Stack, Paper, Button } from '@mui/material';
import { DownloadRounded } from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTenantConfig } from '../../hooks/use-tenant-config';
import { formatGrade } from '../../lib/grading';
import { useQuery } from '@tanstack/react-query';
import { collection, onSnapshot, DocumentData } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useTenantStore } from '../../stores/tenant.store';
import { gradeApi } from '../../lib/api/grade.api';
import { errorText } from '../../lib/error-text';
import { SectionPicker } from './components/SectionPicker';
import { SectionGradebook } from './components/SectionGradebook';

export function GradesPage({
  embeddedSectionId,
  acta = false,
}: {
  embeddedSectionId?: string;
  /** Dentro del ACTA: el libro lleva la cabecera compacta (una sola franja). */
  acta?: boolean;
} = {}) {
  const { t } = useTranslation('sis');
  const { gradingScale } = useTenantConfig();
  const { currentTenantId } = useTenantStore();

  // Embedded mode (inside the /curso cockpit tab): the cockpit owns the section
  // picker + page chrome; this component just renders the gradebook table.
  const embedded = embeddedSectionId !== undefined;

  const [sections, setSections] = useState<DocumentData[]>([]);
  const [courses, setCourses] = useState<DocumentData[]>([]);
  const [terms, setTerms] = useState<DocumentData[]>([]);
  /** Una lectura del catálogo que el servidor rechazó. Se muestra: un selector
   *  vacío sin explicación se lee como «no hay secciones». */
  const [catalogError, setCatalogError] = useState<string | null>(null);

  const [selectedSectionIdState, setSelectedSectionId] = useState<string>('');
  const selectedSectionId = embedded ? (embeddedSectionId ?? '') : selectedSectionIdState;

  // El catálogo de la sección elegida: sección, curso y período. NO hay datos de
  // alumnos acá (PR 3A): los alumnos, las notas y el docente los trae el libro
  // de notas desde el servidor (`getSectionGradebook`), que comprueba que quien
  // pregunta enseña en ESA sección. Antes esta página leía en vivo TODAS las
  // fichas de personas y las inscripciones de la sección.
  useEffect(() => {
    if (!currentTenantId) return;
    const onError = (e: unknown) => setCatalogError(errorText(e));

    const unsubSections = onSnapshot(
      collection(db, `tenants/${currentTenantId}/sections`),
      (s) => setSections(s.docs.map((d) => ({ id: d.id, ...d.data() }))),
      onError,
    );
    const unsubCourses = onSnapshot(
      collection(db, `tenants/${currentTenantId}/courses`),
      (s) => setCourses(s.docs.map((d) => ({ id: d.id, ...d.data() }))),
      onError,
    );
    const unsubTerms = onSnapshot(
      collection(db, `tenants/${currentTenantId}/terms`),
      (s) => setTerms(s.docs.map((d) => ({ id: d.id, ...d.data() }))),
      onError,
    );

    return () => {
      unsubSections();
      unsubCourses();
      unsubTerms();
    };
  }, [currentTenantId]);

  // LA MISMA CONSULTA QUE EL LIBRO (misma clave): el CSV sale de las filas que
  // el docente está viendo, sin una segunda lectura.
  const gradebookQ = useQuery({
    queryKey: ['section-gradebook', currentTenantId, selectedSectionId],
    enabled: !!currentTenantId && !!selectedSectionId,
    queryFn: () =>
      gradeApi.getSectionGradebook({ tenantId: currentTenantId!, sectionId: selectedSectionId }),
    staleTime: 30_000,
  });
  const gradebookRows = gradebookQ.data?.rows ?? [];

  const getCourseName = (id: string) => courses.find((c) => c.id === id)?.name || id;
  const getTermName = (id: string) => terms.find((t) => t.id === id)?.name || id;

  const selectedSection = sections.find((s) => s.id === selectedSectionId);

  const handleExportCSV = () => {
    if (!gradebookRows.length || !selectedSection) return;

    const headers = [
      t('grades.columns.student'),
      t('grades.columns.grade'),
      t('grades.columns.status'),
    ].join(',');

    const rows = gradebookRows.map((r) => {
      const name = r.studentName.replace(/,/g, ''); // strip commas for basic csv
      // Como la escribe el acta, en la escala del seminario.
      const grade = formatGrade(
        { value: r.gradeRecorded, label: r.gradeRecordedLabel },
        gradingScale,
      );
      const status = t(`grades.enrollmentStatus.${r.status}`, r.status) as string;
      return `${name},${grade},${status}`;
    });

    const csvContent = 'data:text/csv;charset=utf-8,\uFEFF' + [headers, ...rows].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);

    // Filename: Fallback clean string
    const courseName = getCourseName(selectedSection.courseId)
      .replace(/[^a-z0-9]/gi, '_')
      .toLowerCase();
    const termName = getTermName(selectedSection.termId)
      .replace(/[^a-z0-9]/gi, '_')
      .toLowerCase();
    link.setAttribute('download', `notas_${courseName}_${termName}.csv`);

    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return (
    <Box sx={{ p: embedded ? 0 : { xs: 2, sm: 3 } }}>
      {!embedded && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          gap={2}
          mb={4}
        >
          <Box>
            <Typography variant="h4" fontWeight={700} color="white">
              {t('grades.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('grades.subtitle')}
            </Typography>
          </Box>
        </Stack>
      )}

      {/* LA CABECERA DEL LIBRO VIVE EN EL LIBRO. Acá había una fila con el
          docente y «Exportar CSV», y ESTA PÁGINA SE RENDERIZA DE DOS MANERAS
          —embebida en Curso y suelta en /grades—, así que era la misma fila
          escrita dos veces. Al mover sólo una, la pantalla de Curso terminó con
          dos botones de CSV. Ahora la fila existe UNA vez, dentro del libro. */}
      {catalogError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {catalogError}
        </Alert>
      )}

      {embedded ? null : (
        /* Select Section Filter */
        <Paper
          sx={{ p: 3, mb: 4, borderRadius: 2, background: 'rgba(255, 255, 255, 0.02)' }}
          variant="outlined"
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ sm: 'center' }}
            flexWrap="wrap"
          >
            <SectionPicker value={selectedSectionId} onChange={setSelectedSectionId} />
          </Stack>
        </Paper>
      )}

      {/* ── EL LIBRO DE NOTAS ──────────────────────────────────────────────
          Reemplaza al diálogo por alumno para lo que importa: la nota final y
          el cierre del curso. La tabla de abajo se conserva porque muestra otra
          cosa —la asistencia— y porque el diálogo por alumno sigue siendo el
          camino para corregir UNA nota suelta sin tocar el resto. */}
      {selectedSectionId && (
        <SectionGradebook
          sectionId={selectedSectionId}
          // El informe impreso necesita nombrarse a sí mismo: sección, período y
          // docente sólo se conocen acá, y un acta sin esos tres datos no
          // identifica de qué curso es.
          {...(selectedSection
            ? {
                sectionLabel: (selectedSection['sectionCode'] as string | undefined) ?? '',
                termLabel: getTermName(selectedSection['termId'] as string),
                ...(acta
                  ? {
                      acta: {
                        courseName: getCourseName(selectedSection['courseId'] as string),
                        termName: getTermName(selectedSection['termId'] as string),
                      },
                    }
                  : {}),
              }
            : {})}
          // Junto al informe PDF, en la cabecera del libro: las dos sacan esta
          // misma tabla del sistema.
          extraActions={
            <Button
              variant="outlined"
              color="secondary"
              size={acta ? 'small' : 'medium'}
              startIcon={<DownloadRounded />}
              onClick={handleExportCSV}
              disabled={gradebookRows.length === 0}
            >
              {t('grades.export')}
            </Button>
          }
        />
      )}

      {/* UNA SOLA LISTA. Acá había una segunda tabla de los mismos alumnos, con
          su propio diálogo para corregir una nota. Se eliminó: ese diálogo
          ofrecía «Nota Final» y «Estado Final», exactamente lo que el libro de
          notas ya edita en línea para los 21 a la vez. Lo único que aportaba de
          verdad —la asistencia y los alumnos retirados— vive ahora dentro del
          libro, como una columna y unas filas marcadas.
          Decisión del director, 2026-09-24: «que sea una sola lista». */}
    </Box>
  );
}
